import { parse, serialize, hasDocumentCookie, parseCookies, readCookie } from './cookies/cookieUtils';

export class Cookies {
    cookies;
    changeListeners = [];

    HAS_DOCUMENT_COOKIE = false;

    cookieConsentTypes = {
        'necessary': 'C0001',
        'performance': 'C0002',
        'functional': 'C0003',
        'targeting': 'C0004',
        'social': 'C0005'
    };

    /**
     *
     * @param {string|object|null} cookies
     * @param {object} options
     */
    constructor(cookies, options) {
        this.cookies = parseCookies(cookies, options);

        new Promise(() => {
            this.HAS_DOCUMENT_COOKIE = hasDocumentCookie();
        }).catch(() => { });
    }

    _updateBrowserValues(parseOptions) {
        if (!this.HAS_DOCUMENT_COOKIE) {
            return;
        }
        this.cookies = parse(document.cookie, parseOptions);
    }

    _emitChange(params) {
        for (let i = 0; i < this.changeListeners.length; ++i) {
            this.changeListeners[i](params);
        }
    }

    get(name, options, parseOptions) {
        this._updateBrowserValues(parseOptions);
        return readCookie(this.cookies[name], options);
    }

    getAll(options, parseOptions) {
        this._updateBrowserValues(parseOptions);
        const result = {};

        for (let name in this.cookies) {
            result[name] = readCookie(this.cookies[name], options);
        }

        return result;
    }

    set(name, value, options) {
        if (typeof value === 'object') {
            value = JSON.stringify(value);
        }

        this.cookies = { ...this.cookies, [name]: value };

        if (this.HAS_DOCUMENT_COOKIE) {
            document.cookie = serialize(name, value, options);
        }

        this._emitChange({ name, value, options });
    }

    remove(name, options) {
        const finalOptions = (options = {
            ...options,
            expires: new Date(1970, 1, 1, 0, 0, 1),
            maxAge: 0,
        });

        this.cookies = { ...this.cookies };
        delete this.cookies[name];

        if (this.HAS_DOCUMENT_COOKIE) {
            document.cookie = serialize(name, '', finalOptions);
        }

        this._emitChange({ name, value: undefined, options });
    }

    userHasConsentedTo(cookieConsentType) {
        const cookieConsentTypeValue = this.cookieConsentTypes[cookieConsentType.toLowerCase()];

        if (!cookieConsentTypeValue) {
            return false;
        }

        const optanonConsentString = this.get('OptanonConsent', {
            domain: process.env.SW_COOKIE_DOMAIN,
            path: '/',
            sameSite: 'Lax',
        });
        if (!optanonConsentString) {
            return true;
        }

        const optanonConsent = new URLSearchParams(optanonConsentString);
        const cookieConsentGroups = optanonConsent
            ?.get('groups')
            ?.split(',')
            ?.map(con => con.split(':'))
            .reduce(
                (obj, [key, val]) => ({
                    ...obj,
                    [key]: Boolean(Number(val)),
                }),
                {},
            );

        if (!cookieConsentGroups) {
            return false;
        }

        return cookieConsentGroups[cookieConsentTypeValue] || false;
    }

    addChangeListener(callback) {
        this.changeListeners.push(callback);
    }

    removeChangeListener(callback) {
        const idx = this.changeListeners.indexOf(callback);
        if (idx >= 0) {
            this.changeListeners.splice(idx, 1);
        }
    }
}
